// Cache global para el precio del oro
let goldPriceCache = {
  usd: null,
  eur: null,
  usdPerGram: null,
  eurPerGram: null,
  lastUpdate: 0,
  date: null,
  source: null
};
const CACHE_DURATION_LBMA = 24 * 60 * 60 * 1000; // 24 horas para LBMA (precio diario)
const CACHE_DURATION_COINGECKO = 10 * 60 * 1000; // 10 minutos para CoinGecko (precio actual)

// Constante: 1 onza troy = 31.1034768 gramos
const OUNCE_TO_GRAMS = 31.1034768;

// Función para obtener el precio del oro en USD y EUR
export const getGoldPrice = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Si no es refresh forzado y tenemos cache válido, devolver cache
  if (!forceRefresh && goldPriceCache.usd) {
    const cacheAge = now - goldPriceCache.lastUpdate;
    const isLBMACache = goldPriceCache.source === 'LBMA';
    const cacheDuration = isLBMACache ? CACHE_DURATION_LBMA : CACHE_DURATION_COINGECKO;
    
    if (cacheAge < cacheDuration) {
      console.log('📦 Usando cache:', {
        source: goldPriceCache.source,
        date: goldPriceCache.date,
        cacheAge: Math.round(cacheAge / 1000 / 60) + ' minutos',
        usd: goldPriceCache.usd,
        eur: goldPriceCache.eur
      });
      
      return {
        usd: goldPriceCache.usd,
        eur: goldPriceCache.eur,
        usdPerGram: goldPriceCache.usdPerGram,
        eurPerGram: goldPriceCache.eurPerGram,
        timestamp: new Date(goldPriceCache.lastUpdate),
        date: goldPriceCache.date,
        source: goldPriceCache.source,
        fromCache: true
      };
    }
  }

  try {
    // Intentar obtener precios de LBMA JSON API primero
    try {
      console.log('🔍 Intentando API JSON de LBMA...');
      const lbmaResponse = await fetch('https://prices.lbma.org.uk/json/gold_pm.json');
      
      if (lbmaResponse.ok) {
        const lbmaData = await lbmaResponse.json();
        console.log('📊 Datos LBMA recibidos, longitud:', lbmaData.length);
        
        if (lbmaData && lbmaData.length > 0) {
          // Obtener el último elemento (precio más reciente)
          const latestPrice = lbmaData[lbmaData.length - 1];
          console.log('📅 Último precio LBMA:', latestPrice);
          
          if (latestPrice && latestPrice.v && latestPrice.v.length >= 3) {
            // v[0] = USD, v[1] = GBP, v[2] = EUR
            const usdPrice = latestPrice.v[0];
            const eurPrice = latestPrice.v[2];
            const date = latestPrice.d;
            
            console.log('✅ Precios LBMA obtenidos - USD:', usdPrice, 'EUR:', eurPrice, 'Fecha:', date);
            
            // Calcular precio por gramo
            const usdPerGram = usdPrice / OUNCE_TO_GRAMS;
            const eurPerGram = eurPrice / OUNCE_TO_GRAMS;
            
            // Actualizar cache global
            goldPriceCache = {
              usd: usdPrice,
              eur: eurPrice,
              usdPerGram: usdPerGram,
              eurPerGram: eurPerGram,
              lastUpdate: now,
              date: date,
              source: 'LBMA'
            };
            
            return {
              usd: usdPrice,
              eur: eurPrice,
              usdPerGram: usdPerGram,
              eurPerGram: eurPerGram,
              timestamp: new Date(now),
              date: date,
              fromCache: false,
              source: 'LBMA'
            };
          } else {
            console.log('❌ Estructura de datos LBMA incorrecta');
          }
        } else {
          console.log('❌ No hay datos en la respuesta LBMA');
        }
      } else {
        console.log('❌ Respuesta LBMA no OK:', lbmaResponse.status);
      }
    } catch (lbmaError) {
      console.log('❌ Error en API LBMA:', lbmaError.message);
    }
    
    // Fallback: usar CoinGecko
    console.log('🔍 Usando CoinGecko como fallback...');
    const usdResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=tether-gold&vs_currencies=usd');
    const usdData = await usdResponse.json();
    
    if (!usdData['tether-gold'] || !usdData['tether-gold'].usd) {
      throw new Error('Datos de precio USD no disponibles');
    }
    
    const usdPrice = usdData['tether-gold'].usd;
    
    // Obtener precio en EUR (Tether Gold)
    const eurResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=tether-gold&vs_currencies=eur');
    const eurData = await eurResponse.json();
    
    if (!eurData['tether-gold'] || !eurData['tether-gold'].eur) {
      throw new Error('Datos de precio EUR no disponibles');
    }
    
    const eurPrice = eurData['tether-gold'].eur;
    
    console.log('💰 Precios CoinGecko obtenidos - USD:', usdPrice, 'EUR:', eurPrice);
    
    // Calcular precio por gramo
    const usdPerGram = usdPrice / OUNCE_TO_GRAMS;
    const eurPerGram = eurPrice / OUNCE_TO_GRAMS;
    
    // Actualizar cache global
    goldPriceCache = {
      usd: usdPrice,
      eur: eurPrice,
      usdPerGram: usdPerGram,
      eurPerGram: eurPerGram,
      lastUpdate: now,
      date: null,
      source: 'CoinGecko'
    };
    
    return {
      usd: usdPrice,
      eur: eurPrice,
      usdPerGram: usdPerGram,
      eurPerGram: eurPerGram,
      timestamp: new Date(now),
      fromCache: false,
      source: 'CoinGecko'
    };
    
  } catch (error) {
    console.error('Error obteniendo precio del oro:', error);
    
    // Si hay error pero tenemos cache, devolver cache
    if (goldPriceCache.usd) {
      return {
        usd: goldPriceCache.usd,
        eur: goldPriceCache.eur,
        usdPerGram: goldPriceCache.usdPerGram,
        eurPerGram: goldPriceCache.eurPerGram,
        timestamp: new Date(goldPriceCache.lastUpdate),
        date: goldPriceCache.date,
        source: goldPriceCache.source,
        fromCache: true,
        error: error.message
      };
    }
    
    // Si no hay cache, devolver error
    throw error;
  }
};

// Función para obtener solo el precio en USD
export const getGoldPriceUSD = async (forceRefresh = false) => {
  const data = await getGoldPrice(forceRefresh);
  return data.usd;
};

// Función para obtener solo el precio en EUR
export const getGoldPriceEUR = async (forceRefresh = false) => {
  const data = await getGoldPrice(forceRefresh);
  return data.eur;
};

// Función para obtener solo el precio por gramo en USD
export const getGoldPricePerGramUSD = async (forceRefresh = false) => {
  const data = await getGoldPrice(forceRefresh);
  return data.usdPerGram;
};

// Función para obtener solo el precio por gramo en EUR
export const getGoldPricePerGramEUR = async (forceRefresh = false) => {
  const data = await getGoldPrice(forceRefresh);
  return data.eurPerGram;
};

// Función para limpiar el cache
export const clearGoldPriceCache = () => {
  goldPriceCache = {
    usd: null,
    eur: null,
    usdPerGram: null,
    eurPerGram: null,
    lastUpdate: 0,
    date: null,
    source: null
  };
};

// Función para verificar si el cache está válido
export const isGoldPriceCacheValid = () => {
  const now = Date.now();
  if (!goldPriceCache.usd) return false;
  
  const cacheAge = now - goldPriceCache.lastUpdate;
  const isLBMACache = goldPriceCache.source === 'LBMA';
  const cacheDuration = isLBMACache ? CACHE_DURATION_LBMA : CACHE_DURATION_COINGECKO;
  
  return cacheAge < cacheDuration;
};
